<template>
  <div class="orders-list">
    <div v-if="loading">
      <list-item loading v-for="item in 10" :key="item"></list-item>
    </div>

    <div v-else>
      <RecycleScroller
        :items="groupedOrders"
        :buffer="screen.tabPort ? 1500 : 500"
        key-field="listId"
        page-mode
        v-slot="{ item }"
      >
        <v-modal
          :animation="!screen.breakPoint.tabPort ? 'slide-up' : 'slide-down'"
          :width="!screen.breakPoint.tabPort ? '': 86"
          :full="!screen.breakPoint.tabPort"
          :noBackground="!screen.breakPoint.tabPort"
          :max-body-height="screen.breakPoint.tabPort ? '70vh' : null"
          block-scroll
          theme="light-grey"
          handle-click-bg
        >
          <template v-slot:activator="{ on }">
            <list-item three-columns @click="on()">
              <template v-slot:groupTitle>
                <div v-if="item.groupDate">
                  {{ item.groupDate }}
                </div>
              </template>

              <template v-slot:firstColumn>
                <list-item-group>
                  <list-item-icon :color="colorTags(item)">
                    {{ item.check ? 'icon-bill' : 'icon-earth' }}
                  </list-item-icon>

                  <list-item-content>
                    <list-item-title>
                      {{ item.user.computedName }}
                    </list-item-title>

                    <list-item-subtitle>
                      <span :class="getOrderStatus(item.status, item.partnerApproved) ? `text-color-${getOrderStatus(item.status, item.partnerApproved).color}` : null">{{ getOrderStatus(item.status, item.partnerApproved) ? getOrderStatus(item.status, item.partnerApproved).value : '—' }}</span>
                    </list-item-subtitle>
                  </list-item-content>
                </list-item-group>
              </template>

              <template v-slot:secondColumn>
                <div v-html="ratingIcon[item.user.rating]"></div>
              </template>

              <template v-slot:thirdColumn>
                <list-item-title font-medium>
                  {{ toCurrency(item.amount, item.currency) }}
                </list-item-title>
                <list-item-subtitle>
                  {{ toCurrency(item.currencyPartnerCommission, item.currency) }}
                </list-item-subtitle>
              </template>

              <template v-slot:controls>
                <kebab-menu v-if="(item.status === 1 || item.status === 3) && item.partnerApproved  === null && item.canChangeStatus && ShowOrdersChangeStatusControls">
                  <action-link color="success" icon-left="icon-check-small" size="small" class="mb-1 pb-05" @click="$emit('accept', { id: item.id })">{{ $t('views.Orders.accept') }}</action-link>
                  <action-link color="error" icon-left="icon-close" size="small" @click="$emit('decline', { id: item.id })">{{ $t('views.Orders.decline') }}</action-link>
                </kebab-menu>

                <div v-if="(item.status === 1) && (item.partnerApproved === false || item.partnerApproved === true) && item.canChangeStatus && ShowOrdersChangeStatusControls">
                  <a href="#" class="icon-undo-small text-color-error text--x-large" @click.prevent="$emit('revert', { id: item.id })"></a>
                </div>
              </template>
            </list-item>
          </template>

          <template v-slot:header>
            <div v-html="toHumanDate(item.orderedAt, 'UTC')"></div>
          </template>

          <div class="d-flex justify-center mt-1">
            <div class="text--center">
              <div class="mb-2">
                <chip :color="getOrderStatus(item.status, item.partnerApproved) ? getOrderStatus(item.status, item.partnerApproved).color : null">{{ getOrderStatus(item.status, item.partnerApproved) ? getOrderStatus(item.status, item.partnerApproved).value : '—' }}</chip>
              </div>

              <div class="heading-3 text--regular mb-1">
                {{ toCurrency(item.amount, item.currency) }}
              </div>

              <div class="text--xx-large">
                {{ toCurrency(item.currencyPartnerCommission, item.currency) }}
              </div>

              <div class="text-color-space text--xs-small text--medium mb-2">
                {{ $t('components.OrderList.commission') }}
              </div>

              <div class="orders-list__voucher mb-2 d-flex justify-center" v-if="item.check">
                <voucher :merchant="profile ? profile.marketingName : null" :data="item.check"></voucher>
              </div>

              <div class="orders-list__order-data text--small mb-3">
                <div class="orders-list__order-data-row">
                  <div class="orders-list__order-data-col">{{ $t('components.OrderList.source') }}</div>
                  <div class="orders-list__order-data-col text--right text--medium" v-if="item.orderType === 1">{{ $t('components.OrderList.receipt') }}</div>
                  <div class="orders-list__order-data-col text--right text--medium" v-else-if="item.orderType === 2">{{ $t('components.OrderList.onlinePurchase') }}</div>
                  <div class="orders-list__order-data-col text--right text--medium" v-else>{{ '--' }}</div>
                </div>
                <div class="orders-list__order-data-row">
                  <div class="orders-list__order-data-col">{{ $t('components.OrderList.clientId') }}</div>
                  <div class="orders-list__order-data-col text--right text--medium">{{ item.user ? item.user.externalUserId : '--' }}</div>
                </div>
                <div class="orders-list__order-data-row">
                  <div class="orders-list__order-data-col mr-5">{{ $t('components.OrderList.customerId') }}</div>
                  <div class="orders-list__order-data-col text--right text--medium">{{ `${item.user.firstName} ${item.user.lastName || '—'} ${item.user.patronymic ? item.user.patronymic : ''}` }}</div>
                </div>
                <div class="orders-list__order-data-row">
                  <div class="orders-list__order-data-col">{{ $t('components.OrderList.customersRating') }}</div>
                  <div class="orders-list__order-data-col text--right text--medium">
                    <div class="d-flex align-center">
                      <div class="text--small mr-05" v-html="ratingIcon[item.user.rating]"></div>
                      <div class="text-color-success text--xs-small mr-05">{{item.user.approvedOrders ? '+' + item.user.approvedOrders : ''}}</div>
                      <div class="text-color-error text--xs-small">{{item.user.declinedOrders ? '-' + item.user.declinedOrders : ''}}</div>
                    </div>
                  </div>
                </div>
                <div class="orders-list__order-data-row">
                  <div class="orders-list__order-data-col mr-5">{{$t('components.ManagersList.newManager.email')}}</div>
                  <div class="orders-list__order-data-col text--right text--medium">{{ item.user.email ? item.user.email : '--' }}</div>
                </div>
              </div>
            </div>
          </div>
        </v-modal>
      </RecycleScroller>
    </div>
  </div>
</template>

<script>
import vModal from '@/ui/modals/vModal'
import KebabMenu from '@/ui/controls/KebabMenu'
import ActionLink from '@/ui/controls/ActionLink'

import ListItem from '@/ui/slots/List/ListItem'
import ListItemGroup from '@/ui/slots/List/ListItemGroup'
import ListItemIcon from '@/ui/slots/List/ListItemIcon'
import ListItemTitle from '@/ui/slots/List/ListItemTitle'
import ListItemContent from '@/ui/slots/List/ListItemContent'
import ListItemSubtitle from '@/ui/slots/List/ListItemSubtitle'

import Chip from '@/ui/slots/Chip'

import Voucher from './Voucher'

import toCurrency from '@/mixins/toCurrency'
import screen from '@/mixins/screen'
import groupByDate from '@/mixins/groupByDate'
import toHumanDate from '@/mixins/toHumanDate'
import profile from '@/mixins/profile'
import { ShowOrdersChangeStatusControls } from '@/mixins/displayRules'

export default {
  mixins: [toCurrency, screen, groupByDate, toHumanDate, profile, ShowOrdersChangeStatusControls],

  components: {
    vModal,
    KebabMenu,
    ActionLink,

    ListItem,

    ListItemGroup,
    ListItemIcon,
    ListItemContent,
    ListItemTitle,
    ListItemSubtitle,

    Voucher,

    Chip
  },

  props: {
    loadingItems: {
      type: Number,
      default: 10
    },

    dateField: {
      type: String,
      default: 'createdAt'
    },
    /**
     * @typedef {Object} OrderCheck
     * @property {string} sourceQr
     */

    /**
     * @typedef {Object} Order
     * @property {number} currencyPartnerCommission
     * @property {number} amount
     * @property {string} currency
     * @property {OrderCheck} check
     * @returns {Order[]}
     */
    orders: {
      type: Array,
      default: () => []
    },

    loading: {
      type: Boolean,
      default: false
    },

    titleMaxLength: {
      type: Number,
      default: 15
    }
  },

  methods: {
    colorTags (item) {
      const check = item.check

      if (check) {
        if (+this.profile.checkAmountMax < check.amount && this.profile.checkAmountMax !== null) {
          return '#FF647C'
        } else if (+this.profile.checkAmountMaxCalculated < check.amount && this.profile.checkAmountMaxCalculated !== null) {
          return '#FF647C'
        } else if (check.manual) {
          return '#0084F8'
        } else if (item.statusReason === 21) {
          return '#34303D'
        }
      }
    },

    getOrderStatus (status, partnerApprove) {
      if (partnerApprove === null && status === 1) {
        return {
          color: 'neutral',
          value: this.$i18n.t('components.OrderList.pending')
        }
      } else if (partnerApprove === true && status === 1) {
        return {
          color: 'info',
          value: this.$i18n.t('components.OrderList.accepted')
        }
      } else {
        return {
          color: this.STATUS[status] ? this.STATUS[status].color : this.STATUS[6].color,
          value: this.STATUS[status] ? this.STATUS[status].value : this.STATUS[6].value
        }
      }
    }
  },

  computed: {
    STATUS () {
      return {
        2: {
          value: this.$i18n.t('components.OrderList.approved'),
          color: 'success'
        },
        6: {
          value: this.$i18n.t('components.OrderList.checking'),
          color: 'info'
        },
        3: {
          value: this.$i18n.t('components.OrderList.declined'),
          color: 'error'
        },
        1: {
          value: this.$i18n.t('components.OrderList.pending'),
          color: 'neutral'
        }
      }
    },

    ratingIcon () {
      return {
        GOOD: '<i class="icon-good text-color-green"></i>',
        NEUTRAL: '<i class="icon-neutral text-color-yellow"></i>',
        BAD: '<i class="icon-bad text-color-red"></i>',
        EMPTY: ' - '
      }
    },

    groupedOrders () {
      const itemSize = this.screen.breakPoint.hugeDesktop ? 72 : 65
      const groupItemSize = this.screen.breakPoint.hugeDesktop ? 123 : 106
      const orders = this.orders

      if (!this.orders) {
        return []
      }

      const ordersMapped = orders.map(order => {
        const totalOrders = (Math.abs(order.user.approvedOrders) / (Math.abs(order.user.approvedOrders) + Math.abs(order.user.declinedOrders)) * 100)

        order.user.computedName = `${order.user.firstName} ${order.user.lastName || '—'} ${order.user.patronymic ? order.user.patronymic : ''}`

        const targetLength = this.titleMaxLength
        if (order.user.computedName.length > targetLength) {
          order.user.computedName = `${order.user.computedName.substr(0, targetLength - 3)}...`
        }

        if (order.user.approvedOrders === 0 && order.user.declinedOrders === 0) {
          order.user.rating = 'EMPTY'
        } else if (totalOrders > 50) {
          order.user.rating = 'GOOD'
        } else if (totalOrders <= 50 && totalOrders >= 20) {
          order.user.rating = 'NEUTRAL'
        } else if (totalOrders < 20) {
          order.user.rating = 'BAD'
        }

        return order
      })

      return this.groupByDate(ordersMapped, this.dateField, itemSize, groupItemSize)
    }
  }
}
</script>

<style lang="scss">
  .orders-list {
    margin-left: -$default-spacing-mobile;
    margin-right: -$default-spacing-mobile;

    @include respond(tab-port) {
      margin-left: -$default-spacing;
      margin-right: -$default-spacing;
    }

    &__group {
      &:not(:last-child) {
        margin-bottom: 2rem;
      }
    }

    &__order-data {
      &-row {
        display: flex;
        justify-content: space-between;
        border-bottom: 2px $color-azure dashed;
        padding-top: 1rem;
        padding-bottom: 1rem;
        text-align: left;
      }
    }
  }
</style>
